import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Partner Sale Promotions (managers+). Create / edit / toggle / delete a
 * PartnerPromotion for a retail partner, and view its redemptions. The API
 * enforces managers-only writes; this page is also gated to managers.
 *
 * First pass supports the only rule type today (BOGO_PERCENT: `percent` off the
 * item at position `qty`, e.g. 50% off the 2nd bag).
 */
interface Promotion {
  id: number;
  partner: number;
  partner_name: string;
  name: string;
  is_active: boolean;
  visible_from: string;
  redeem_start: string;
  redeem_end: string;
  rule_type: string;
  percent: number;
  qty: number;
  applies_to: string;
  title: string;
  message: string;
  status: string;
  redemption_count: number;
}

interface Partner { id: number; name: string }

interface Redemption {
  id: number;
  member_name: string;
  member_code: string;
  status: string;
  redeemed_at: string | null;
  used_at_store_name: string | null;
  discount_value_recorded: string | null;
}

const emptyForm = {
  partner: '', name: '', title: '', message: '',
  visible_from: '', redeem_start: '', redeem_end: '',
  percent: '50', qty: '2', applies_to: '2nd bag, any product', is_active: false,
};

// Flatten a DRF error body ({field: [msgs]} or {non_field_errors:[...]} or string).
function extractError(e: any, fallback: string): string {
  const d = e?.response?.data;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d?.non_field_errors)) return d.non_field_errors[0];
  if (d?.detail) return d.detail;
  if (d?.error) return d.error;
  const first = Object.entries(d)[0];
  if (first) return `${first[0]}: ${Array.isArray(first[1]) ? first[1][0] : first[1]}`;
  return fallback;
}

const PartnerPromotionsAdmin: React.FC = () => {
  const { user } = useAuth();
  const isManager = !!(user?.is_superuser || user?.role === 'ADMIN');

  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<typeof emptyForm>(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [redemptionsFor, setRedemptionsFor] = useState<Promotion | null>(null);
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pRes, oRes] = await Promise.all([
        api.get('/admin/partner-promotions/'),
        api.get('/admin/partner-promotions/partners/'),
      ]);
      // DRF list may be paginated ({results}) or a plain array.
      setPromotions(pRes.data?.results ?? pRes.data ?? []);
      setPartners(oRes.data?.partners ?? []);
    } catch (e: any) {
      setError(extractError(e, 'Could not load promotions.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isManager) load(); else setLoading(false); }, [isManager, load]);

  const resetForm = () => { setForm(emptyForm); setEditingId(null); };

  // Client-side date-order hint (backend is authoritative and 400s on bad order).
  const dateOrderOk = (() => {
    const { visible_from: v, redeem_start: s, redeem_end: e } = form;
    if (!v || !s || !e) return true;
    return v <= s && s <= e;
  })();

  const save = async () => {
    if (!form.name.trim() || !form.title.trim() || !form.partner) {
      setError('Partner, name, and title are required.'); return;
    }
    if (!form.visible_from || !form.redeem_start || !form.redeem_end) {
      setError('All three dates are required.'); return;
    }
    setSaving(true);
    setError(null);
    const payload = {
      partner: Number(form.partner), name: form.name, title: form.title, message: form.message,
      visible_from: form.visible_from, redeem_start: form.redeem_start, redeem_end: form.redeem_end,
      rule_type: 'BOGO_PERCENT', percent: Number(form.percent) || 0, qty: Number(form.qty) || 1,
      applies_to: form.applies_to, is_active: form.is_active,
    };
    try {
      if (editingId) {
        await api.patch(`/admin/partner-promotions/${editingId}/`, payload);
      } else {
        await api.post('/admin/partner-promotions/', payload);
      }
      resetForm();
      await load();
    } catch (e: any) {
      setError(extractError(e, 'Could not save the promotion.'));
    } finally {
      setSaving(false);
    }
  };

  const edit = (p: Promotion) => {
    setEditingId(p.id);
    setForm({
      partner: String(p.partner), name: p.name, title: p.title, message: p.message,
      visible_from: p.visible_from, redeem_start: p.redeem_start, redeem_end: p.redeem_end,
      percent: String(p.percent), qty: String(p.qty), applies_to: p.applies_to, is_active: p.is_active,
    });
    setRedemptionsFor(null);
  };

  const toggleActive = async (p: Promotion) => {
    try { await api.patch(`/admin/partner-promotions/${p.id}/`, { is_active: !p.is_active }); await load(); }
    catch (e: any) { setError(extractError(e, 'Could not change active state.')); }
  };

  const remove = async (p: Promotion) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await api.delete(`/admin/partner-promotions/${p.id}/`); await load(); }
    catch (e: any) { setError(extractError(e, 'Could not delete.')); }
  };

  const viewRedemptions = async (p: Promotion) => {
    setRedemptionsFor(p);
    setRedemptions([]);
    try {
      const res = await api.get(`/admin/partner-promotions/${p.id}/redemptions/`);
      setRedemptions(res.data ?? []);
    } catch (e: any) { setError(extractError(e, 'Could not load redemptions.')); }
  };

  if (!isManager) {
    return (
      <div style={{ maxWidth: 820, margin: '0 auto', padding: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', color: '#1a4d1a' }}>Partner Promotions</h1>
        <p style={{ color: '#b91c1c' }}>Manager access is required to manage partner promotions.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '1.5rem' }}>
      <h1 style={{ fontSize: '1.5rem', color: '#1a4d1a' }}>Partner Promotions</h1>
      <p style={{ color: '#6b7280', marginTop: 0 }}>
        Member-appreciation offers redeemed at a partner's register. Red Bridge records the one-time
        redemption per member; the cashier keys the actual discount on their own POS.
      </p>
      {error && <div style={{ color: '#b91c1c', margin: '8px 0' }} role="alert">{error}</div>}

      {/* Create / edit form */}
      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>{editingId ? 'Edit promotion' : 'New promotion'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <label>Internal name
            <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              style={{ width: '100%', padding: 6 }} placeholder="e.g. Yelm Member Appreciation" />
          </label>
          <label>Partner
            <select value={form.partner} onChange={e => setForm({ ...form, partner: e.target.value })}
              style={{ width: '100%', padding: 6 }}>
              <option value="">— select partner —</option>
              {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <label>Visible from
            <input type="date" value={form.visible_from} onChange={e => setForm({ ...form, visible_from: e.target.value })}
              style={{ width: '100%', padding: 6 }} />
          </label>
          <label>Redeem start
            <input type="date" value={form.redeem_start} onChange={e => setForm({ ...form, redeem_start: e.target.value })}
              style={{ width: '100%', padding: 6 }} />
          </label>
          <label>Redeem end
            <input type="date" value={form.redeem_end} onChange={e => setForm({ ...form, redeem_end: e.target.value })}
              style={{ width: '100%', padding: 6 }} />
          </label>
          <label>Percent off
            <input type="number" min="0" max="100" value={form.percent} onChange={e => setForm({ ...form, percent: e.target.value })}
              style={{ width: '100%', padding: 6 }} />
          </label>
          <label>Applies to which item (qty)
            <input type="number" min="1" value={form.qty} onChange={e => setForm({ ...form, qty: e.target.value })}
              style={{ width: '100%', padding: 6 }} />
          </label>
          <label>Applies to (description)
            <input type="text" value={form.applies_to} onChange={e => setForm({ ...form, applies_to: e.target.value })}
              style={{ width: '100%', padding: 6 }} />
          </label>
          <label style={{ gridColumn: '1 / 3' }}>Member-facing title
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              style={{ width: '100%', padding: 6 }} placeholder="e.g. 50% off your 2nd bag" />
          </label>
          <label style={{ gridColumn: '1 / 3' }}>Member-facing message
            <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
              rows={2} style={{ width: '100%', padding: 6 }} placeholder="Customer Appreciation" />
          </label>
        </div>
        {!dateOrderOk && (
          <div style={{ color: '#b45309', marginTop: 8, fontSize: 13 }}>
            Dates should be ordered: visible from ≤ redeem start ≤ redeem end.
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 10 }}>
          <label><input type="checkbox" checked={form.is_active}
            onChange={e => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
          <button onClick={save} disabled={saving}
            style={{ padding: '6px 14px', background: '#1a4d1a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
            {saving ? 'Saving…' : editingId ? 'Save changes' : 'Create'}
          </button>
          {editingId && <button onClick={resetForm} style={{ padding: '6px 12px' }}>Cancel</button>}
        </div>
      </div>

      {/* List */}
      {loading ? <p>Loading…</p> : promotions.length === 0 ? (
        <p style={{ color: '#6b7280' }}>No promotions yet.</p>
      ) : (
        <table style={{ width: '100%', fontSize: 14, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>
              <th>Name</th><th>Partner</th><th>Redeem window</th><th>Status</th><th>Redemptions</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {promotions.map(p => (
              <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td>{p.name}</td>
                <td>{p.partner_name}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{p.redeem_start} → {p.redeem_end}</td>
                <td>{p.is_active
                  ? <span style={{ color: '#166534', fontWeight: 700 }}>{p.status}</span>
                  : <span style={{ color: '#9ca3af' }}>inactive</span>}</td>
                <td>{p.redemption_count}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button onClick={() => edit(p)}>Edit</button>{' '}
                  <button onClick={() => toggleActive(p)}>{p.is_active ? 'Deactivate' : 'Activate'}</button>{' '}
                  <button onClick={() => viewRedemptions(p)}>Redemptions</button>{' '}
                  <button onClick={() => remove(p)} style={{ color: '#b91c1c' }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Redemptions panel */}
      {redemptionsFor && (
        <div style={{ marginTop: 20, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Redemptions — {redemptionsFor.name}</h3>
            <button onClick={() => setRedemptionsFor(null)}>Close</button>
          </div>
          {redemptions.length === 0 ? (
            <p style={{ color: '#6b7280' }}>No redemptions yet.</p>
          ) : (
            <table style={{ width: '100%', fontSize: 14, borderCollapse: 'collapse', marginTop: 8 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>
                  <th>Member</th><th>Code</th><th>Status</th><th>Redeemed</th><th>Store</th><th>Discount recorded</th>
                </tr>
              </thead>
              <tbody>
                {redemptions.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td>{r.member_name}</td>
                    <td>{r.member_code}</td>
                    <td>{r.status}</td>
                    <td>{r.redeemed_at ? new Date(r.redeemed_at).toLocaleString() : '—'}</td>
                    <td>{r.used_at_store_name || '—'}</td>
                    <td>{r.discount_value_recorded ? `$${r.discount_value_recorded}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default PartnerPromotionsAdmin;
